import { ChangeEvent, KeyboardEvent, ReactNode, useId } from 'react'
import { PageControlBarModule, TabMenu, EmissionsSummaryPanelModule } from '../../components/BaseComponents'
import { CarbonAccountSummary } from '../../types/CarbonAccountSummary'
import { getCurrencySymbol } from '../../lib/utils'
import useCurrency from '../../hooks/useCurrency'

export type ProjectDetailZones = {
	primarySelector: { label: string; controlId: string; children: ReactNode }
	viewSelector: ReactNode
	summary: ReactNode
	tabs: ReactNode
	filtersSimple: ReactNode
	actions: ReactNode
	table: ReactNode
}

type UseProjectDetailZonesArgs = {
	projectSelector: ReactNode
	summary: any
	// Issue #884: loadSummary() previously had no catch, so a rejecting GET (including the
	// now-rejecting ACCESS_AUDIT_UNAVAILABLE notify-only code - #857 item 6) left the whole page
	// stuck on a full-page spinner forever (see CompanyProject.tsx's `if(loadingSummary)` gate).
	// Mirrors useProjectsListZones' summaryError so a failed summary load degrades to a visible
	// error on the summary panel instead, same as the projects list page.
	summaryError?: any
	scope: string
	onScopeChange: (scope: string) => void
	grouping: 'purchases' | 'suppliers'
	onGroupingChange: (grouping: 'purchases' | 'suppliers') => void
	searchText: string
	onSearchChange: (event: ChangeEvent<HTMLInputElement>) => void
	onSearchKeyDown: (event: KeyboardEvent<HTMLInputElement>) => void
	onSearchClear: () => void
	searchInProgress: boolean
	tableComponent: ReactNode
	isLoading: boolean
	onEditClick: () => void
}

export function useProjectDetailZones({
	projectSelector,
	summary,
	summaryError,
	scope,
	onScopeChange,
	grouping,
	onGroupingChange,
	searchText,
	onSearchChange,
	onSearchKeyDown,
	onSearchClear,
	searchInProgress,
	tableComponent,
	isLoading,
	onEditClick,
}: UseProjectDetailZonesArgs): ProjectDetailZones {
	const currency = useCurrency()
	const projectSelectorId = useId()

	const errorText = (error: any): string | undefined => {
		if (error === undefined || error === null) {
			return undefined
		}
		if (typeof error === 'string') {
			return error
		}
		return error.message ?? String(error)
	}

	// Placeholder/label mirror the granularity, matching Carbon Accounts' per-view search config.
	const search = grouping === 'suppliers'
		? { name: 'searchProjectSuppliers', label: 'Search suppliers', placeholder: 'Search suppliers' }
		: { name: 'searchProjectItems', label: 'Search items', placeholder: 'Search items' }

	return {
		primarySelector: {
			label: 'Select Project',
			controlId: projectSelectorId,
			children: projectSelector,
		},
		viewSelector: (
			<PageControlBarModule.RadioGroup
				label='View'
				name='company-project-grouping'
				options={[
					{
						id: 'suppliers',
						label: 'Suppliers',
						checked: grouping === 'suppliers',
						onChange: () => onGroupingChange('suppliers'),
					},
					{
						id: 'purchases',
						label: 'Items',
						checked: grouping === 'purchases',
						onChange: () => onGroupingChange('purchases'),
					},
				]}
			/>
		),
		summary: (
			<EmissionsSummaryPanelModule
				carbonSummary={{
					intensityRatio: String(summary?.intensity ?? 0),
					localIntensityRatio: String(summary?.intensity ?? 0),
					scope1Total: String(summary?.scope1Total ?? 0),
					scope2Total: String(summary?.scope2Total ?? 0),
					scope3Total: String(summary?.scope3Total ?? 0),
					scope1Completion: String(summary?.scope1Completion ?? 0),
					scope2Completion: String(summary?.scope2Completion ?? 0),
					scope3Completion: String(summary?.scope3Completion ?? 0),
					totalCompletion: String(summary?.totalCompletion ?? 0),
				} as CarbonAccountSummary}
				totalEmissions={summary?.totalEmissions}
				intensityRatioUnit={`${getCurrencySymbol(currency)} of project value`}
				activeScope={scope as '' | '1' | '2' | '3'}
				error={errorText(summaryError)}
			/>
		),
		tabs: (
			<TabMenu
				tabs={[
					{ id: '', label: 'Overview' },
					{ id: '1', label: 'Scope 1' },
					{ id: '2', label: 'Scope 2' },
					{ id: '3', label: 'Scope 3 (upstream)' },
				]}
				value={scope}
				onChange={onScopeChange}
				ariaLabel='Emissions scope'
				testId='scope-tabs'
				additionalClass='hidden sm:block'
			/>
		),
		filtersSimple: (
			<PageControlBarModule.Primary>
				<PageControlBarModule.Search
					name={search.name}
					label={search.label}
					placeholder={search.placeholder}
					value={searchText}
					onChange={onSearchChange}
					onKeyDown={onSearchKeyDown}
					onClear={onSearchClear}
					isLoading={searchInProgress}
				/>
			</PageControlBarModule.Primary>
		),
		actions: (
			<PageControlBarModule.Button
				requiresWrite
				type='Primary'
				text='Edit project'
				icon='fa-regular fa-pen-to-square'
				hasLeadingIcon
				onClick={onEditClick}
			/>
		),
		table: tableComponent,
	}
}
